import { sendAnalyticsEvent, useSearch } from '@faststore/sdk'
import { useSession } from 'src/sdk/session'
import { gql } from '@faststore/graphql-utils'
import { useMemo } from 'react'
import { useQuery } from 'src/sdk/graphql/useQuery'
import type {
  IStoreSelectedFacet,
  ProductGalleryQueryQuery as Query,
  ProductGalleryQueryQueryVariables as Variables,
} from '@generated/graphql'
import type { State, SearchSort } from '@faststore/sdk/dist/types'
import type { IntelligentSearchQueryEvent } from 'src/sdk/analytics/types'
import { useLocalizedVariables } from 'src/sdk/product/useProductsQuery'
// import { getCookie } from 'src/utils/cookie'

/**
 * This query is run on the browser and contains
 * the current search state of the user
 */
export const query = gql`
  fragment SearchEvent_metadata on SearchMetadata {
    isTermMisspelled
    logicalOperator
  }

  query ProductGalleryQuery(
    $first: Int!
    $after: String!
    $sort: StoreSort!
    $term: String!
    $selectedFacets: [IStoreSelectedFacet!]!
  ) {
    search(
      first: $first
      after: $after
      sort: $sort
      term: $term
      selectedFacets: $selectedFacets
    ) {
      products {
        pageInfo {
          totalCount
          hasNextPage
        }
        edges {
          node {
            id: productID
            breadcrumbList {
              itemListElement {
                name
              }
            }
            slug
            sku
            brand {
              brandName: name
            }
            name
            description
            gtin
            releaseDate
            productClusters {
              id
              name
            }
            clusterHighlights {
              id
              name
            }
            isVariantOf {
              name
              productGroupID
              additionalProperty {
                propertyID
                name
                value
                valueReference
              }
              hasVariant {
                ...skuFragment
              }
            }
            additionalProperty {
              propertyID
              name
              value
              valueReference
            }
            image {
              url
              alternateName
            }
            brand {
              name
            }
            offers {
              lowPrice
              offers {
                availability
                price
                listPrice
                quantity
                priceValidUntil
                seller {
                  identifier
                }
              }
            }
          }
        }
      }
      facets {
        ...Filter_facets
      }
      metadata {
        ...SearchEvent_metadata
      }
    }
  }
`

const toArray = <T>(x: T[] | T | undefined) =>
  Array.isArray(x) ? x : x ? [x] : []

interface CustomGalleryQueryProps {
  state: State
  itemsPerPage: number
}

export const useCustomGalleryQuery = ({
  state,
  itemsPerPage,
}: CustomGalleryQueryProps) => {
  const { term, sort, selectedFacets, page } = state
  const { channel } = useSession()

  if (!channel) {
    throw new Error(`useProduct: 'channel' from session is an empty string.`)
  }

  const variables = useMemo(() => {
    const facets = toArray(selectedFacets)

    return {
      first: itemsPerPage,
      after: (itemsPerPage * page).toString(),
      sort,
      term: term ?? '',
      selectedFacets: [...facets, { key: 'channel', value: channel }],
    }
  }, [channel, itemsPerPage, page, sort, term, selectedFacets])

  return useQuery<Query, Variables>(query, variables)
}

export const useGalleryQuery = (
  originalSelectedFacets?: IStoreSelectedFacet[],
  sort?: SearchSort
) => {
  const {
    state: { term, sort: sortSearch, selectedFacets, page },
    itemsPerPage,
  } = useSearch()

  // const uuidToUse = getCookie('uuid')
  const { locale } = useSession()
  const localizedVariables = useLocalizedVariables({
    first: itemsPerPage,
    after: (itemsPerPage * page).toString(),
    sort: sort ?? sortSearch,
    term: term ?? '',
    selectedFacets: originalSelectedFacets ?? selectedFacets,
  })

  return useQuery<Query, Variables>(query, localizedVariables, {
    onSuccess: (data) => {
      if (data && term) {
        sendAnalyticsEvent<IntelligentSearchQueryEvent>({
          name: 'intelligent_search_query',
          params: {
            locale,
            term,
            url: window.location.href,
            logicalOperator: data.search.metadata?.logicalOperator ?? 'and',
            isTermMisspelled: data.search.metadata?.isTermMisspelled ?? false,
            totalCount: data.search.products.pageInfo.totalCount,
            // uuid: uuidToUse,
          },
        })
      }
    },
  })
}
